import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { translate, type AppLocale, type MessageKey } from '../i18n/index.js';
import {
  getSettings,
  migrateLegacyLocalStorage,
  openVifDb,
  saveSettings,
  type VifDB,
} from '../storage/index.js';
import { DEFAULT_SETTINGS, settingsFromStored, type AppSettings } from './settings.js';
import { ensureFontLoaded } from './fonts.js';
import { applyTheme } from './theme.js';

/**
 * App services: one shared db connection, the settings store (idb-backed),
 * and the typed i18n catalog. The §6.6 legacy migration runs once before
 * first render so migrated settings are visible immediately.
 */

interface Services {
  readonly db: VifDB;
  readonly settings: AppSettings;
  readonly updateSettings: (patch: Partial<AppSettings>) => void;
  readonly t: (key: MessageKey, params?: Readonly<Record<string, string | number>>) => string;
  readonly locale: AppLocale;
}

const ServicesContext = createContext<Services | null>(null);

export function useServices(): Services {
  const services = useContext(ServicesContext);
  if (services === null) throw new Error('useServices outside <AppServices>');
  return services;
}

interface Boot {
  readonly db: VifDB;
  readonly settings: AppSettings;
}

async function boot(): Promise<Boot> {
  const db = await openVifDb();
  try {
    await migrateLegacyLocalStorage(db, window.localStorage);
  } catch {
    // Migration is best-effort; a blocked localStorage must not stop boot.
  }
  const stored = await getSettings(db);
  return { db, settings: settingsFromStored(stored.data) };
}

export function AppServices({ children }: { children: ReactNode }): ReactNode {
  const [ready, setReady] = useState<Boot | null>(null);
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);

  useEffect(() => {
    let cancelled = false;
    void boot().then((result) => {
      if (cancelled) return;
      setSettings(result.settings);
      setReady(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    applyTheme(settings.theme);
  }, [settings.theme]);

  // H4: non-default reading fonts fetch lazily on first selection.
  useEffect(() => {
    void ensureFontLoaded(settings.font);
  }, [settings.font]);

  const updateSettings = useCallback(
    (patch: Partial<AppSettings>) => {
      setSettings((current) => {
        const next = { ...current, ...patch };
        if (ready !== null) {
          void getSettings(ready.db).then((stored) =>
            saveSettings(ready.db, { ...stored.data, ...next }),
          );
        }
        return next;
      });
    },
    [ready],
  );

  const services = useMemo<Services | null>(() => {
    if (ready === null) return null;
    return {
      db: ready.db,
      settings,
      updateSettings,
      locale: settings.appLanguage,
      t: (key, params) => translate(settings.appLanguage, key, params),
    };
  }, [ready, settings, updateSettings]);

  if (services === null) {
    return <div className="boot" />;
  }
  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
}
